// Reglas ópticas de la Historia Clínica Visual. Espejo de backend/apps/clinical_records/optometria.py:
// el servidor vuelve a validar, esto solo avisa al profesional antes de guardar.

const PLANO = ['PL', 'PLANO', 'PLANA', 'NEUTRO', 'N'];
const ES_PASO = (d) => Math.abs(Math.round(d * 4) - d * 4) < 1e-9;
const formato = (d) => `${d >= 0 ? '+' : '-'}${Math.abs(d).toFixed(2)}`;

const dioptria = (valor) => {
    const texto = String(valor || '').trim().toUpperCase().replace(',', '.').replace(/\s/g, '');
    if (!texto) return null;
    if (PLANO.includes(texto)) return 0;
    if (!/^[+-]?\d+(\.\d+)?$/.test(texto)) throw new Error(`"${valor}" no es un valor dióptrico válido.`);
    return parseFloat(texto);
};

export const normalizarEsfera = (v) => {
    const d = dioptria(v);
    if (d === null) return '';
    if (Math.abs(d) > 30) throw new Error(`La esfera ${v} está fuera de rango (±30 D).`);
    if (!ES_PASO(d)) throw new Error(`La esfera ${v} debe ir en pasos de 0.25 D.`);
    return d === 0 ? 'PLANO' : formato(d);
};

export const normalizarCilindro = (v) => {
    const d = dioptria(v);
    if (d === null || d === 0) return '';
    if (Math.abs(d) > 10) throw new Error(`El cilindro ${v} está fuera de rango (±10 D).`);
    if (!ES_PASO(d)) throw new Error(`El cilindro ${v} debe ir en pasos de 0.25 D.`);
    return formato(d);
};

export const normalizarEje = (v) => {
    const texto = String(v || '').trim().replace(/[°º]/g, '');
    if (!texto) return '';
    if (!/^\d+$/.test(texto)) throw new Error(`El eje "${v}" debe ser un número entero entre 1 y 180.`);
    let eje = parseInt(texto, 10);
    if (eje === 0) eje = 180;
    if (eje < 1 || eje > 180) throw new Error(`El eje ${v} debe estar entre 1 y 180.`);
    return String(eje);
};

export const normalizarAdd = (v) => {
    const d = dioptria(v);
    if (d === null || d === 0) return '';
    if (d < 0) throw new Error('La adición siempre es positiva.');
    if (d < 0.5 || d > 4) throw new Error(`La adición ${v} está fuera de rango (+0.50 a +4.00).`);
    if (!ES_PASO(d)) throw new Error(`La adición ${v} debe ir en pasos de 0.25 D.`);
    return formato(d);
};

export const normalizarDp = (v) => {
    const texto = String(v || '').trim().toLowerCase().replace('mm', '').replace(/,/g, '.').replace(/\s/g, '');
    if (!texto) return '';
    const partes = texto.split('/');
    const nums = partes.map(Number);
    if (nums.some(isNaN) || partes.length > 2) throw new Error(`La D.P. "${v}" debe ser en mm, ej: 62 o 31/31.`);
    if (nums.length === 1 && (nums[0] < 40 || nums[0] > 85)) throw new Error(`La D.P. ${v} mm está fuera de rango (40-85 mm).`);
    if (nums.length === 2 && nums.some(n => n < 20 || n > 43)) throw new Error(`La D.P. monocular ${v} está fuera de rango (20-43 mm por ojo).`);
    return nums.join('/');
};

export const normalizarPio = (v) => {
    const texto = String(v || '').trim().toLowerCase().replace('mmhg', '').replace(',', '.').replace(/\s/g, '');
    if (!texto) return '';
    const d = Number(texto);
    if (isNaN(d)) throw new Error(`La PIO "${v}" debe ser un número en mmHg.`);
    if (d < 3 || d > 70) throw new Error(`La PIO ${v} mmHg está fuera de rango (3-70).`);
    return String(d);
};

const NORMALIZADORES = {
    esfera: normalizarEsfera, cilindro: normalizarCilindro, eje: normalizarEje, add: normalizarAdd, pio: normalizarPio
};

// Normaliza un campo al salir de él (onBlur). Si el valor es inválido se deja tal cual; validarFormula lo reportará.
export const normalizarCampo = (name, value) => {
    if (name === 'dp') { try { return normalizarDp(value); } catch (e) { return value; } }
    if (name === 'add_val') { try { return normalizarAdd(value); } catch (e) { return value; } }
    const m = /^(esfera|cilindro|eje|add|pio)_(od|oi)$/.exec(name);
    if (!m) return value;
    try { return NORMALIZADORES[m[1]](value); } catch (e) { return value; }
};

const TIPOS_CON_ADICION = /bifocal|progresiv|ocupacional/i;

// Devuelve { datos, errores: [mensajes], campos: {campo: mensaje} } con las mismas reglas que el servidor.
// `campos` permite marcar en rojo exactamente el campo que hay que corregir.
export const validarFormula = (form) => {
    const datos = { ...form };
    const errores = [];
    const campos = {};
    const error = (campo, mensaje) => {
        errores.push(mensaje);
        if (!campos[campo]) campos[campo] = mensaje;
    };
    const aplicar = (campo, fn, prefijo = '') => {
        if (!(campo in form)) return;
        try { datos[campo] = fn(form[campo]); } catch (e) { error(campo, prefijo + e.message); }
    };
    ['od', 'oi'].forEach(ojo => {
        const nombre = ojo === 'od' ? 'O.D' : 'O.I';
        const p = `${nombre}: `;
        aplicar(`esfera_${ojo}`, normalizarEsfera, p);
        aplicar(`cilindro_${ojo}`, normalizarCilindro, p);
        aplicar(`eje_${ojo}`, normalizarEje, p);
        aplicar(`add_${ojo}`, normalizarAdd, p);
        aplicar(`pio_${ojo}`, normalizarPio, p);
        const cil = datos[`cilindro_${ojo}`];
        const eje = datos[`eje_${ojo}`];
        if (cil && !eje && !campos[`eje_${ojo}`]) error(`eje_${ojo}`, `${nombre}: hay cilindro sin eje. La fórmula no se puede tallar.`);
        if (eje && !cil && `cilindro_${ojo}` in form) datos[`eje_${ojo}`] = '';
        if (cil && !datos[`esfera_${ojo}`]) datos[`esfera_${ojo}`] = 'PLANO';
    });
    aplicar('add_val', normalizarAdd);
    aplicar('dp', normalizarDp);
    if (TIPOS_CON_ADICION.test(form.tipo_lentes || '') && !datos.add_od && !datos.add_oi) {
        const mensaje = `Un lente "${form.tipo_lentes}" necesita adición (ADD) en el RX final.`;
        error('add_od', mensaje);
        error('add_oi', mensaje);
        errores.pop();
        campos.tipo_lentes = mensaje;
    }
    return { datos, errores, campos };
};

const numero = (v) => { try { const d = dioptria(v); return d === null ? null : d; } catch (e) { return null; } };
const snellen = (av) => { const m = /^20\/(\d+)$/.exec(String(av || '').trim()); return m ? parseInt(m[1], 10) : null; };

// Hallazgos que no bloquean el guardado pero que el profesional debe ver (posible derivación).
export const advertenciasClinicas = (f) => {
    const avisos = [];
    const addOd = numero(f.add_od), addOi = numero(f.add_oi);
    if (addOd !== null && addOi !== null && Math.abs(addOd - addOi) > 0.5) avisos.push('La adición de O.D y O.I difiere más de 0.50 D; normalmente es igual en ambos ojos.');
    const eqOd = (numero(f.esfera_od) || 0) + (numero(f.cilindro_od) || 0) / 2;
    const eqOi = (numero(f.esfera_oi) || 0) + (numero(f.cilindro_oi) || 0) / 2;
    if ((f.esfera_od || f.esfera_oi) && Math.abs(eqOd - eqOi) >= 2) avisos.push(`Anisometropía de ${Math.abs(eqOd - eqOi).toFixed(2)} D entre ojos: vigilar tolerancia (aniseiconia) y riesgo de ambliopía en niños.`);
    ['od', 'oi'].forEach(ojo => {
        const nombre = ojo === 'od' ? 'O.D' : 'O.I';
        const pio = parseFloat(f[`pio_${ojo}`]);
        if (pio > 21) avisos.push(`${nombre}: PIO de ${pio} mmHg (> 21). Considerar derivación a oftalmología para descartar glaucoma.`);
        const avcc = snellen(f[`avl_${ojo}`]) || snellen(f[`av_cc_${ojo}_lejos`]);
        if (avcc && avcc > 40) avisos.push(`${nombre}: la mejor AV corregida es 20/${avcc} (peor que 20/40). Investigar causa orgánica / derivar.`);
    });
    const pioOd = parseFloat(f.pio_od), pioOi = parseFloat(f.pio_oi);
    if (!isNaN(pioOd) && !isNaN(pioOi) && Math.abs(pioOd - pioOi) > 4) avisos.push('Diferencia de PIO entre ojos mayor a 4 mmHg.');
    return avisos;
};

// Potencia equivalente en el plano corneal (lentes de contacto) a partir del RX en gafas a 12 mm.
// Solo relevante cuando algún meridiano supera ±4.00 D.
export const potenciaLenteContacto = (esfera, cilindro, distanciaMm = 12) => {
    const esf = numero(esfera);
    if (esf === null) return null;
    const cil = numero(cilindro) || 0;
    const d = distanciaMm / 1000;
    const vertice = (F) => Math.round((F / (1 - d * F)) * 4) / 4;
    const m1 = esf, m2 = esf + cil;
    if (Math.abs(m1) < 4 && Math.abs(m2) < 4) return null;
    const e = vertice(m1);
    const c = vertice(m2) - e;
    return c ? `${formato(e)} ${formato(c)}` : formato(e);
};
